import { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import styles from './TableShutdownBanner.module.scss';

/**
 * Table shutdown / maintenance banner (Molecules tier). A STATIC, dismissible
 * warning bar — unlike the lobby MaintenanceMarquee (which continuously
 * scrolls), this holds still so a critical "table is closing" notice stays
 * readable, and it PERSISTS until the table closes rather than auto-hiding
 * after a few seconds.
 *
 * Styled in the app's Outlaw Solid language (orange gradient, cream lead glyph,
 * dashed bottom rail, Oswald uppercase) so it reads like the rest of the
 * scrolling notifications / MaintenanceMarquee instead of a dull dark bar.
 *
 * Presentational only: the parent supplies the copy and decides when to mount
 * it (i.e. when the table is flagged for shutdown). Dismissal is local — once
 * the player closes it, it stays closed for this mount.
 */
/**
 * EN-10249 AC2 specifies this string EXACTLY. It was hard-coded at each render site,
 * so the AC held only for as long as nobody edited one of them — and a drifting copy
 * is invisible in review because each site looks correct on its own.
 *
 * Must stay byte-identical to SHUTDOWN_MSG in
 * serverless-v2/services/table-manager/lib/gracefulShutdown.js, which is the value the
 * server writes to the table's msg field. Bound by a test rather than by a comment;
 * see TableShutdownBanner.copy.test.ts.
 */
export const TABLE_SHUTDOWN_MESSAGE =
  'This table will close after the current hand due to scheduled maintenance. Please finish the current hand and return to the lobby to continue playing.';

type Props = {
  /** The shutdown notice copy. Kept as a prop so copy (incl. the Western
   *  variant) is owned by the caller / config, not baked into the component.
   *  Callers showing the AC2 notice should pass TABLE_SHUTDOWN_MESSAGE. */
  message: string;
  /** Optional dismiss hook so a parent can persist the dismissed state per
   *  table. When omitted the banner still hides itself locally. */
  onDismiss?: () => void;
};

const TableShutdownBanner = ({ message, onDismiss }: Props) => {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || !message.trim()) return null;

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <div className={styles.bar} role='status' aria-live='polite' aria-label='table shutdown notice'>
      <span aria-hidden='true' className={styles.rail} />
      <span className={styles.icon} aria-hidden='true'>
        {/* Wrench glyph — matches the maintenance semantics of MaintenanceMarquee. */}
        <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
          <path d='M21.7 18.6 14.4 11.3a5.5 5.5 0 0 0-6.9-7l3.2 3.2-1.8 1.8-3.2-3.2a5.5 5.5 0 0 0 7 6.9l7.3 7.3a1 1 0 0 0 1.4 0l.3-.3a1 1 0 0 0 0-1.4z' />
        </svg>
      </span>
      <span className={styles.copy}>{message}</span>
      <button
        type='button'
        className={styles.close}
        aria-label='Dismiss shutdown notice'
        onClick={handleDismiss}>
        <CloseIcon fontSize='small' />
      </button>
    </div>
  );
};

export default TableShutdownBanner;
